import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface MailboxSentData {
  email: string
  client_name: string
  sent_today: number
  sent_7d: number
  sent_14d: number
  total_sent: number
}

export function useMailboxSentAnalytics() {
  return useQuery({
    queryKey: ['mailbox-sent-analytics'],
    queryFn: async (): Promise<MailboxSentData[]> => {
      console.log('Fetching mailbox sent analytics...')
      
      // Get all mailboxes with their clients
      const { data: mailboxes, error: mailboxError } = await supabase
        .from('mailboxes')
        .select('email, client_id')

      if (mailboxError) {
        console.error('Error fetching mailboxes:', mailboxError)
        throw mailboxError
      }

      if (!mailboxes || mailboxes.length === 0) {
        console.log('No mailboxes found')
        return []
      }

      console.log(`Found ${mailboxes.length} mailboxes`)

      // Get the date ranges we need
      const now = new Date()
      const today = new Date(now)
      today.setHours(0, 0, 0, 0)
      
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      const sevenDaysAgo = new Date(today)
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6) // Include today in 7-day count
      
      const fourteenDaysAgo = new Date(today)
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 13) // Include today in 14-day count

      // Fetch sent events for all mailboxes
      const { data: sentEvents, error: sentError } = await supabase
        .from('obsync_dynamic_reports')
        .select('from_email, created_at')
        .eq('event_type', 'EMAIL_SENT')
        .in('from_email', mailboxes.map(m => m.email))
        .order('created_at', { ascending: false })

      if (sentError) {
        console.error('Error fetching sent events:', sentError)
        throw sentError
      }

      console.log(`Found ${sentEvents?.length || 0} sent events`)

      // Create a map to aggregate sent counts
      const sentMap = new Map<string, {
        email: string
        client_name: string
        sent_today: number
        sent_7d: number
        sent_14d: number
        total_sent: number
      }>()

      // Initialize all mailboxes in the map
      mailboxes.forEach(mailbox => {
        sentMap.set(mailbox.email, {
          email: mailbox.email,
          client_name: (mailbox as any).client_id?.toString() || '',
          sent_today: 0,
          sent_7d: 0,
          sent_14d: 0,
          total_sent: 0
        })
      })

      // Process sent events
      sentEvents?.forEach(event => {
        const mailboxData = sentMap.get(event.from_email)
        if (!mailboxData) return

        const eventDate = new Date(event.created_at)
        
        // Count total
        mailboxData.total_sent++
        
        // Count by time periods
        if (eventDate >= today) {
          mailboxData.sent_today++
        }
        if (eventDate >= sevenDaysAgo) {
          mailboxData.sent_7d++
        }
        if (eventDate >= fourteenDaysAgo) {
          mailboxData.sent_14d++
        }
      })

      // Convert map to array and filter out mailboxes with no sent emails
      const result = Array.from(sentMap.values()).filter(mailbox => mailbox.total_sent > 0)

      console.log(`Processed ${result.length} mailboxes with sent emails`)

      // Sort by total sent (highest first), then by today's sent, then by 7-day sent
      return result.sort((a, b) => {
        if (a.total_sent !== b.total_sent) {
          return b.total_sent - a.total_sent
        }
        if (a.sent_today !== b.sent_today) {
          return b.sent_today - a.sent_today
        }
        return b.sent_7d - a.sent_7d
      })
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  })
}